import Swiper from 'swiper';
import { Navigation, Autoplay } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
import { readAttribute } from '@helga-agency/ui-tools';

/* global window, HTMLElement */

interface NavigationButtons {
    prevEl: HTMLElement;
    nextEl: HTMLElement;
}

export default class QuoteSlider extends HTMLElement {
    swiper: Swiper | null = null;
    swiperParams: SwiperOptions | null = null;

    connectedCallback() {
        const sliderElement = this.getSliderElement();
        const autoplayInterval = this.getAttribute('data-quote-slider-interval');

        this.swiperParams = {
            modules: [Navigation, Autoplay],
            slidesPerView: 1.05,
            grabCursor: true,
            navigation: this.getNavigationButtons(),
            on: {
                navigationPrev: () => {
                    this.disableAutoplay();
                },
                navigationNext: () => {
                    this.disableAutoplay();
                },
            },
            breakpoints: {
                600: {
                    slidesPerView: 1,
                },
            },
        };

        if (autoplayInterval) {
            this.swiperParams.loop = true;
            this.swiperParams.autoplay = {
                delay: parseInt(autoplayInterval, 10),
            };
        }

        // eslint-disable-next-line no-new
        this.swiper = new Swiper(sliderElement, this.swiperParams);
    }

    private disableAutoplay(): void {
        if (this.swiper && this.swiper.autoplay.running) {
            // Save current real index
            const { realIndex } = this.swiper;

            this.swiper.autoplay?.stop();

            // save the swiper element and config
            const swiperEl = this.swiper.el;
            const currentConfig = this.swiper.params;

            this.swiper.once('slideChangeTransitionEnd', () => {
                // Destroy and recreate the swiper, since the loop
                // does something weird to the indices, so we can't
                // just remove it from the current instance
                this.swiper?.destroy(true, false);

                this.swiper = new Swiper(swiperEl, {
                    ...currentConfig,
                    loop: false,
                    autoplay: false,
                    initialSlide: realIndex,
                });
            });
        }
    }

    private getSliderElement(): HTMLElement {
        return readAttribute<HTMLElement>(this, 'data-quote-slider-element-selector', {
            transform: (value) => this.querySelector<HTMLElement>(value),
            validate: (element: HTMLElement | null) => element instanceof HTMLElement,
            expectation: 'a selector that matches the element for data-quote-slider-element-selector',
        });
    }

    private getNavigationButtons(): NavigationButtons {
        const getButton = (selector: string): HTMLElement => {
            return readAttribute<HTMLElement>(this, selector, {
                transform: (value) => this.querySelector<HTMLElement>(value),
                validate: (element: HTMLElement) => element instanceof HTMLElement,
                expectation: `a selector that matches the element for ${selector}`,
            });
        };

        return {
            prevEl: getButton('data-quote-slider-prev-button-selector'),
            nextEl: getButton('data-quote-slider-next-button-selector'),
        };
    }

    public static defineElement(): void {
        if (!window.customElements.get('quote-slider')) {
            window.customElements.define('quote-slider', QuoteSlider);
        }
    }
}
